import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { X, Gift, Phone, User, Sparkles, CheckCircle, MessageCircle, Clock } from "lucide-react";
import { courseCategories } from "@/data/courses";

const API = process.env.REACT_APP_BACKEND_URL;
const SHOWN_KEY = "vedanti_exit_intent_shown_v1";
const DISCOUNT = "Free Counselling + ₹500 Off on Course Fee";

// Detect when user is about to leave (desktop = mouse to top, mobile = scroll-up after engagement)
const ExitIntentPopup = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", course_interest: "" });
  const lastScrollY = useRef(0);
  const engagedAt = useRef(Date.now());

  // Reset engagement timer on route change (but keep "shown once" flag)
  useEffect(() => { engagedAt.current = Date.now(); }, [pathname]);

  // Hide on form pages (we don't want to interrupt active form fills)
  const hiddenRoutes = ["/demo-class", "/consultancy", "/contact", "/study-material"];
  const isHidden = hiddenRoutes.includes(pathname);

  const tryShow = useCallback(() => {
    if (isHidden) return;
    if (sessionStorage.getItem(SHOWN_KEY)) return;
    // Require at least 8s of engagement before showing
    if (Date.now() - engagedAt.current < 8000) return;
    sessionStorage.setItem(SHOWN_KEY, "1");
    setOpen(true);
  }, [isHidden]);

  useEffect(() => {
    if (isHidden) return;
    // Desktop: mouseout to top edge (proper exit-intent signal)
    const onMouseOut = (e) => {
      // Fired when mouse leaves the document toward the top
      if (e.clientY <= 0 && !e.relatedTarget && !e.toElement) tryShow();
    };
    // Mobile: rapid scroll up after scrolling down (back-to-top intent)
    const onScroll = () => {
      const y = window.scrollY;
      const goingUpFast = lastScrollY.current - y > 80;
      const scrolledEnough = lastScrollY.current > 500;
      if (goingUpFast && scrolledEnough) tryShow();
      lastScrollY.current = y;
    };
    // Inactivity fallback: 45s on page = show
    const inactivityTimer = setTimeout(tryShow, 45000);
    document.addEventListener("mouseout", onMouseOut);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
      clearTimeout(inactivityTimer);
    };
  }, [tryShow, isHidden]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || form.name.trim().length < 2) {
      setError("Please enter your full name");
      return;
    }
    const phoneClean = form.phone.replace(/\D/g, "");
    if (phoneClean.length < 10) {
      setError("Please enter a valid 10-digit phone number");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/leads/exit-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          phone: phoneClean,
          course_interest: form.course_interest || null,
          source_page: pathname,
          discount_offered: DISCOUNT,
        }),
      });
      if (!res.ok) throw new Error("Submission failed");
      setSubmitted(true);
    } catch (err) {
      setError("Could not submit — please call us at +91 8308776505 directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-fade-in"
      data-testid="exit-intent-popup"
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-intent-title"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-vedanti-navy/85 backdrop-blur-sm" onClick={() => setOpen(false)}></div>

      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-fade-in-up">
        {/* Close */}
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-vedanti-navy flex items-center justify-center transition-all hover:scale-110 shadow-md"
          aria-label="Close popup"
          data-testid="exit-intent-close"
        >
          <X size={18} />
        </button>

        {/* Header */}
        <div className="relative overflow-hidden bg-gradient-to-br from-vedanti-orange via-amber-500 to-vedanti-mango px-6 pt-8 pb-12 text-center">
          <div className="absolute -top-6 -right-6 w-32 h-32 bg-white/10 rounded-full"></div>
          <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-white/10 rounded-full"></div>
          <div className="relative">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 mb-3 border border-white/30">
              <Sparkles size={14} className="text-white" />
              <span className="text-white text-xs font-bold uppercase tracking-wider">Wait! Before you leave</span>
            </div>
            <h2 id="exit-intent-title" className="font-heading font-extrabold text-white text-2xl md:text-3xl leading-tight drop-shadow">
              Grab a Free Counselling Call + <span className="text-vedanti-navy bg-white px-2 rounded-md inline-block mt-1">₹500 Off</span>
            </h2>
            <p className="text-white/90 text-sm mt-3 leading-relaxed">
              Our expert mentor will call you within 30 minutes to plan your selection journey.
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 -mt-6 pb-6">
          {!submitted ? (
            <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl border border-slate-100 p-5 space-y-3">
              {/* Trust strip */}
              <div className="flex items-center justify-center gap-4 text-[11px] text-slate-500 font-medium pb-1">
                <span className="inline-flex items-center gap-1"><CheckCircle size={12} className="text-green-600" /> 31+ Achievers</span>
                <span className="inline-flex items-center gap-1"><CheckCircle size={12} className="text-green-600" /> 500+ 5★ Reviews</span>
              </div>

              {/* Name */}
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your full name"
                  className="w-full pl-9 pr-3 py-3 rounded-xl border border-slate-200 focus:outline-none focus:border-vedanti-orange focus:ring-2 focus:ring-vedanti-orange/20 text-sm"
                  data-testid="exit-intent-name"
                  required
                  autoFocus
                />
              </div>

              {/* Phone */}
              <div className="relative">
                <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="WhatsApp number (10 digits)"
                  className="w-full pl-9 pr-3 py-3 rounded-xl border border-slate-200 focus:outline-none focus:border-vedanti-orange focus:ring-2 focus:ring-vedanti-orange/20 text-sm"
                  data-testid="exit-intent-phone"
                  required
                  maxLength={15}
                  inputMode="tel"
                />
              </div>

              {/* Course interest */}
              <div className="relative">
                <Gift size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <select
                  value={form.course_interest}
                  onChange={(e) => setForm({ ...form, course_interest: e.target.value })}
                  className="w-full pl-9 pr-3 py-3 rounded-xl border border-slate-200 focus:outline-none focus:border-vedanti-orange focus:ring-2 focus:ring-vedanti-orange/20 text-sm appearance-none bg-white"
                  data-testid="exit-intent-course"
                >
                  <option value="">Interested in… (optional)</option>
                  {courseCategories.map((c) => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                  <option value="Not sure yet">Not sure yet — please guide me</option>
                </select>
              </div>

              {error && (
                <p className="text-red-600 text-xs font-medium" data-testid="exit-intent-error">{error}</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-vedanti-orange to-amber-500 text-white font-bold py-3.5 rounded-xl shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:translate-y-0 inline-flex items-center justify-center gap-2"
                data-testid="exit-intent-submit"
              >
                {submitting ? "Sending…" : (<>Claim My ₹500 Off <Sparkles size={16} /></>)}
              </button>

              <p className="text-center text-[11px] text-slate-400 pt-1 flex items-center justify-center gap-1.5">
                <Clock size={11} /> We&apos;ll call within 30 minutes • No spam, ever
              </p>
            </form>
          ) : (
            <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6 text-center" data-testid="exit-intent-success">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-green-500 to-emerald-400 rounded-full flex items-center justify-center mb-4 shadow-lg shadow-green-500/30">
                <CheckCircle size={32} className="text-white" />
              </div>
              <h3 className="font-heading font-bold text-vedanti-navy text-xl mb-2">You&apos;re all set, {form.name.split(" ")[0]}!</h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-5">
                Our mentor will call you within <strong>30 minutes</strong> on <strong>{form.phone}</strong> with your ₹500-off counselling slot.
              </p>
              <a
                href={`https://wa.me/918308776505?text=${encodeURIComponent(`Hi Vedanti Academy, I just submitted my details (${form.name}) and would like to confirm my free counselling call.`)}`}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-5 py-3 rounded-xl font-semibold transition-all shadow-md"
                data-testid="exit-intent-whatsapp"
              >
                <MessageCircle size={18} /> Or chat with us now on WhatsApp
              </a>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="block mx-auto mt-4 text-slate-400 hover:text-vedanti-navy text-xs font-medium"
                data-testid="exit-intent-dismiss-success"
              >
                Continue browsing
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
